// API Service for GovernWell

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

class ApiService {
  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  getToken() {
    return localStorage.getItem('authToken');
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Network error' }));
      let detail = error.detail || 'Something went wrong';
      if (Array.isArray(detail)) {
        detail = detail.map((d) => d?.msg || JSON.stringify(d)).join(', ');
      } else if (detail && typeof detail === 'object') {
        detail = JSON.stringify(detail);
      }
      throw new Error(detail);
    }

    return response.json();
  }

  // Auth endpoints
  async signup(email, password, orgName, organisationType) {
    return this.request('/api/auth/signup', {
      method: 'POST',
      body: JSON.stringify({
        email,
        password,
        org_name: orgName,
        ...(organisationType ? { organisation_type: organisationType } : {}),
      }),
    });
  }

  async login(email, password) {
    return this.request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async getProfile() {
    return this.request('/api/auth/me');
  }

  async logout() {
    return this.request('/api/auth/logout', { method: 'POST' });
  }

  // Dashboard endpoints
  async getDashboard() {
    return this.request('/api/dashboard');
  }

  // Review endpoints
  async createReview(name) {
    return this.request('/api/reviews', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  async getReviews() {
    return this.request('/api/reviews');
  }

  async getReview(reviewId) {
    return this.request(`/api/reviews/${reviewId}`);
  }

  async deleteReview(reviewId) {
    return this.request(`/api/reviews/${reviewId}`, { method: 'DELETE' });
  }

  async getPhaseData(reviewId, phaseNumber) {
    return this.request(`/api/reviews/${reviewId}/phase/${phaseNumber}`);
  }

  async updatePhaseData(reviewId, phaseNumber, phaseData) {
    return this.request(`/api/reviews/${reviewId}/phase/${phaseNumber}`, {
      method: 'PUT',
      body: JSON.stringify({ phase_data: phaseData }),
    });
  }

  async completePhase(reviewId, phaseNumber) {
    return this.request(`/api/reviews/${reviewId}/phase/${phaseNumber}/complete`, {
      method: 'POST',
    });
  }

  // Improvement Plan - bypasses phase validation
  async updateImprovementPlan(reviewId, planData) {
    return this.request(`/api/reviews/${reviewId}/improvement-plan`, {
      method: 'PUT',
      body: JSON.stringify({ phase_data: planData }),
    });
  }

  // File upload endpoints
  async uploadFile(reviewId, category, file, description = '') {
    const url = `${this.baseUrl}/api/files/upload`;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('review_id', reviewId);
    formData.append('category', category);
    if (description) {
      formData.append('description', description);
    }

    const token = this.getToken();
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
      throw new Error(error.detail || 'Failed to upload file');
    }

    return response.json();
  }

  async getReviewFiles(reviewId) {
    return this.request(`/api/files/review/${reviewId}`);
  }

  async getCategoryFiles(reviewId, category) {
    return this.request(`/api/files/review/${reviewId}/category/${category}`);
  }

  async deleteFile(fileId) {
    return this.request(`/api/files/${fileId}`, { method: 'DELETE' });
  }

  // Team management endpoints
  async getTeamMembers() {
    return this.request('/api/team');
  }

  async inviteTeamMember(email, role = 'member') {
    return this.request('/api/team/invite', {
      method: 'POST',
      body: JSON.stringify({ email, role }),
    });
  }

  async updateMemberRole(memberId, role) {
    return this.request(`/api/team/${memberId}/role?role=${role}`, {
      method: 'PUT',
    });
  }

  async removeTeamMember(memberId) {
    return this.request(`/api/team/${memberId}`, { method: 'DELETE' });
  }

  // Report generation endpoints
  async generateReport(reviewId) {
    const url = `${this.baseUrl}/api/reports/generate/${reviewId}`;
    const token = this.getToken();
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to generate report' }));
      throw new Error(error.detail || 'Failed to generate report');
    }

    // Return blob for download
    return response.blob();
  }

  async getReportPreview(reviewId) {
    return this.request(`/api/reports/preview/${reviewId}`);
  }

  // ===== Entity governance endpoints =====
  async getEntityConfigAll() {
    return this.request('/api/entity/config');
  }

  async getEntityConfig(entityType) {
    return this.request(`/api/entity/config/${entityType}`);
  }

  async getOrganisationType() {
    return this.request('/api/entity/organisation-type');
  }

  async setOrganisationType(organisationType) {
    return this.request('/api/entity/organisation-type', {
      method: 'PUT',
      body: JSON.stringify({ organisation_type: organisationType }),
    });
  }

  async getEntityDashboard() {
    return this.request('/api/entity/dashboard');
  }

  async createEntityReview(name = 'Governance Review') {
    return this.request('/api/entity/reviews', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  async getEntityReviews() {
    return this.request('/api/entity/reviews');
  }

  async getCurrentEntityReview() {
    return this.request('/api/entity/current-review');
  }

  async getEntityReview(reviewId) {
    return this.request(`/api/entity/reviews/${reviewId}`);
  }

  async deleteEntityReview(reviewId) {
    return this.request(`/api/entity/reviews/${reviewId}`, { method: 'DELETE' });
  }

  async saveEntityAssessment(reviewId, answers) {
    return this.request(`/api/entity/reviews/${reviewId}/assessment`, {
      method: 'PUT',
      body: JSON.stringify({ answers }),
    });
  }

  async saveEntitySections(reviewId, sections) {
    return this.request(`/api/entity/reviews/${reviewId}/sections`, {
      method: 'PUT',
      body: JSON.stringify(sections),
    });
  }

  // Questionnaire export / import
  async exportAssessment(reviewId) {
    const url = `${this.baseUrl}/api/entity/reviews/${reviewId}/assessment/export`;
    const token = this.getToken();
    const response = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || 'Export failed');
    }
    const blob = await response.blob();
    const disposition = response.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="?([^";]+)"?/);
    const filename = match ? match[1] : 'governwell-questionnaire.xlsx';
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(link.href);
  }

  async importAssessment(reviewId, file) {
    const url = `${this.baseUrl}/api/entity/reviews/${reviewId}/assessment/import`;
    const formData = new FormData();
    formData.append('file', file);
    const token = this.getToken();
    const response = await fetch(url, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || 'Import failed');
    }
    return response.json();
  }

  // Observation (meeting effectiveness) endpoints
  async listObservations(reviewId) {
    return this.request(`/api/entity/reviews/${reviewId}/observations`);
  }

  async uploadObservation(reviewId, file) {
    const url = `${this.baseUrl}/api/entity/reviews/${reviewId}/observations`;
    const formData = new FormData();
    formData.append('file', file);
    const token = this.getToken();
    const response = await fetch(url, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || 'Upload failed');
    }
    return response.json();
  }

  async deleteObservation(documentId) {
    return this.request(`/api/entity/observations/${documentId}`, { method: 'DELETE' });
  }

  // Attendance analysis endpoints
  async listAttendanceDocuments(reviewId) {
    return this.request(`/api/entity/reviews/${reviewId}/attendance`);
  }

  async uploadAttendanceDocument(reviewId, file) {
    const url = `${this.baseUrl}/api/entity/reviews/${reviewId}/attendance`;
    const formData = new FormData();
    formData.append('file', file);
    const token = this.getToken();
    const response = await fetch(url, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || 'Upload failed');
    }
    return response.json();
  }

  async deleteAttendanceDocument(documentId) {
    return this.request(`/api/entity/attendance/${documentId}`, { method: 'DELETE' });
  }

  async listEntityDocuments(reviewId) {
    return this.request(`/api/entity/reviews/${reviewId}/documents`);
  }

  async uploadEntityDocument(reviewId, documentType, file) {
    const url = `${this.baseUrl}/api/entity/reviews/${reviewId}/documents`;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', documentType);
    const token = this.getToken();
    const response = await fetch(url, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
      throw new Error(error.detail || 'Failed to upload document');
    }
    return response.json();
  }

  async deleteEntityDocument(documentId) {
    return this.request(`/api/entity/documents/${documentId}`, { method: 'DELETE' });
  }

  async generateEntityReview(reviewId) {
    return this.request(`/api/entity/reviews/${reviewId}/generate`, { method: 'POST' });
  }

  async getEntityGeneratedReview(reviewId) {
    return this.request(`/api/entity/reviews/${reviewId}/review`);
  }

  // ===== Governance Health Check (lead funnel) =====
  async getHealthCheckQuestions() {
    return this.request('/api/health-check/questions');
  }

  async submitHealthCheck({ full_name, organisation_name, role, email, consent, responses }) {
    return this.request('/api/health-check/submit', {
      method: 'POST',
      body: JSON.stringify({ full_name, organisation_name, role, email, consent, responses }),
    });
  }

  async getHealthCheckLeads() {
    return this.request('/api/health-check/leads');
  }

  async downloadHealthCheckReport(leadId) {
    const url = `${this.baseUrl}/api/health-check/report/${leadId}`;
    const response = await fetch(url);
    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ detail: 'Failed to generate report' }));
      throw new Error(error.detail || 'Failed to generate report');
    }
    const blob = await response.blob();
    const disposition = response.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="?([^"]+)"?/i);
    const filename = match ? match[1] : 'governance_health_check.pdf';
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(objectUrl);
  }

  async downloadEntityReport(reviewId) {
    const url = `${this.baseUrl}/api/reports/entity/${reviewId}`;
    const token = this.getToken();
    const response = await fetch(url, {
      method: 'GET',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to generate report' }));
      throw new Error(error.detail || 'Failed to generate report');
    }
    return response.blob();
  }
}

export const api = new ApiService();
export default api;
